import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Renderable } from '@components/Renderable';
import { Lifetime } from '@components/Lifetime';
import { Collider } from '@components/Collider';
import { Tag } from '@components/Tag';
import { BulletTrail } from '@components/BulletTrail';
import { World } from '@ecs/World';
import { Damage } from '@components/Damage';
import { Effects } from '@/effects/Effects';

export class WeaponUtils {
  static isInViewport(position: Position, canvas: HTMLCanvasElement): boolean {
    return position.x >= 0 && position.x <= canvas.width &&
           position.y >= 0 && position.y <= canvas.height;
  }
  static createBullet(
    world: World,
    from: Position,
    to: Position,
    bulletSpeed: number,
    bulletColor: string,
    isPlayerBullet: boolean,
    damage: number = 1
  ): void {
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    WeaponUtils.createBulletAtAngle(world, from, angle, bulletSpeed, bulletColor, isPlayerBullet, damage);
  }

  static createBulletAtAngle(
    world: World,
    from: Position,
    angle: number,
    bulletSpeed: number,
    bulletColor: string,
    isPlayerBullet: boolean,
    damage: number = 1
  ): void {
    const bullet = new Entity()
      .addComponent(new Position(
        from.x + Math.cos(angle) * 15,
        from.y + Math.sin(angle) * 15
      ))
      .addComponent(new Velocity(angle, bulletSpeed))
      .addComponent(new Renderable('bolt', bulletColor, Math.min(6, 2 + damage), undefined, 0, bulletColor, 8))
      .addComponent(new Lifetime(150, 150))
      .addComponent(new Collider(
        8, 
        isPlayerBullet ? 'playerBullet' : 'enemyBullet',
        isPlayerBullet ? ['enemy'] : ['player']
      ))
      .addComponent(new Tag('bullet'))
      .addComponent(new BulletTrail())
      .addComponent(new Damage(damage));

    world.addEntity(bullet);
  }

  /** Angles for a fan of projectiles centred on `baseAngle`. */
  static spreadAngles(baseAngle: number, count: number, spread: number): number[] {
    if (count <= 1) return [baseAngle];
    const step = spread / (count - 1);
    return Array.from({ length: count }, (_, i) => baseAngle - spread / 2 + step * i);
  }

  static createMuzzleFlash(world: World, position: Position, angle: number, color: string = '#00ffff'): void {
    Effects.muzzleFlash(
      world,
      position.x + Math.cos(angle) * 15,
      position.y + Math.sin(angle) * 15,
      angle,
      color
    );
  }

  static getDistance(a: Position, b: Position): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  static findNearestTarget(
    world: World, 
    position: Position, 
    range: number, 
    targetTag: string
  ): { entity: Entity, position: Position } | null {
    const entities = world.getEntitiesWithComponents(Position, Tag);
    let nearest: { entity: Entity, position: Position } | null = null;
    let minDistance = range;

    for (const entity of entities) {
      const tag = entity.getComponent(Tag)!;
      if (tag.value !== targetTag) continue;

      const targetPos = entity.getComponent(Position)!;
      const distance = WeaponUtils.getDistance(position, targetPos);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = { entity, position: targetPos };
      }
    }

    return nearest;
  }
}